import React, { useEffect, useMemo, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import setWith from "lodash/set";
import getPath from "lodash/get";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BrandLogo } from "@/components/helios/BrandLogo";
import { adminApi, useSiteContent } from "@/context/SiteContent";
import { ICON_OPTIONS } from "@/components/helios/icons";
import { Lock, Loader2, Save, Plus, Trash2, LogOut, ArrowLeft, Eye, EyeOff, Inbox } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 9);

const TABS = [
  ["company", "Company Info"],
  ["banner", "Announcement"],
  ["analytics", "Analytics & SEO"],
  ["security", "Security"],
  ["social", "Social Links"],
  ["impact", "Impact Counters"],
  ["products", "Products"],
  ["om", "O&M Services"],
  ["gallery", "Gallery"],
  ["clients", "Clients"],
  ["badges", "Badges"],
  ["testimonials", "Testimonials"],
  ["roi", "ROI Defaults"],
  ["faqs", "FAQs"],
  ["leads", "Leads"],
];

const Label = ({ children }) => <span className="block text-xs font-medium text-slate-500 mb-1.5">{children}</span>;

function Field({ label, value, onChange, type = "text", placeholder }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <Input type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
        className="rounded-lg border-helios-line" />
    </label>
  );
}
function Area({ label, value, onChange, rows = 3 }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="rounded-lg border-helios-line" />
    </label>
  );
}
function Toggle({ label, value, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className={`inline-flex items-center gap-2 rounded-lg border px-3 h-10 text-sm font-medium transition-colors ${value ? "border-helios-cyan bg-helios-cyan/10 text-helios-cyan" : "border-helios-line text-slate-500"}`}>
      {value ? <Eye size={15} /> : <EyeOff size={15} />} {label}: {value ? "On" : "Off"}
    </button>
  );
}
const Card = ({ children }) => <div className="rounded-2xl border border-helios-line bg-white p-6 space-y-4">{children}</div>;

// specs array <-> "label | value" text
const specsToText = (specs) => (specs || []).map((s) => `${s.label} | ${s.value}`).join("\n");
const textToSpecs = (t) => t.split("\n").map((l) => l.split("|")).filter((p) => p[0]?.trim()).map((p) => ({ label: (p[0] || "").trim(), value: (p[1] || "").trim() }));
const listToText = (arr) => (arr || []).join("\n");
const textToList = (t) => t.split("\n").map((x) => x.trim()).filter(Boolean);

function LoginGate({ onOk }) {
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.login(pass);
      const data = await adminApi.fetchAdminContent(pass);
      onOk(pass, data);
    } catch {
      toast.error("Incorrect passcode.");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-helios-mist px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-helios-line bg-white p-8" data-testid="admin-login">
        <div className="flex justify-center mb-6"><BrandLogo size={38} /></div>
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-helios-cyan/10 text-helios-cyan mx-auto mb-4"><Lock size={22} /></div>
        <h1 className="font-display text-xl font-bold text-helios-slate text-center">Admin Access</h1>
        <p className="text-sm text-slate-500 text-center mt-1 mb-5">Enter the passcode to manage site content.</p>
        <Input type="password" placeholder="Passcode" value={pass} onChange={(e) => setPass(e.target.value)}
          className="rounded-lg border-helios-line" data-testid="admin-passcode" autoFocus />
        <Button type="submit" disabled={loading} className="btn-sheen mt-4 w-full rounded-full bg-helios-slate hover:bg-helios-slate/90 h-11" data-testid="admin-login-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Unlock Dashboard"}
        </Button>
        <Link to="/" className="mt-4 block text-center text-sm text-slate-400 hover:text-helios-slate">← Back to site</Link>
      </form>
    </div>
  );
}

export default function AdminPage() {
  const { refresh } = useSiteContent();
  const [pass, setPass] = useState(adminApi.getPass());
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("company");
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    // try existing session silently
    const p = adminApi.getPass();
    if (p) adminApi.fetchAdminContent(p).then((d) => { setPass(p); setDraft(d); }).catch(() => adminApi.logout());
  }, []);

  const upd = (path, value) => setDraft((d) => { const n = cloneDeep(d); setWith(n, path, value, Object); return n; });
  const g = (path, fallback) => getPath(draft, path, fallback);

  const save = async () => {
    setSaving(true);
    try {
      await axiosSave(draft, pass);
      await refresh();
      toast.success("Content saved & published.");
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Session expired — re-enter passcode." : "Save failed.");
    } finally { setSaving(false); }
  };

  const loadLeads = async () => {
    try { setLeads(await adminApi.fetchLeads(pass)); }
    catch { toast.error("Could not load leads."); }
  };

  if (!draft) return <LoginGate onOk={(p, d) => { setPass(p); setDraft(d); }} />;

  return (
    <div className="min-h-screen bg-helios-mist">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-[64px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <BrandLogo size={30} />
            <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-widest text-slate-400">CMS Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-helios-slate" data-testid="admin-back-home"><ArrowLeft size={15} /> Site</Link>
            <button onClick={() => { adminApi.logout(); setDraft(null); }} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-500"><LogOut size={15} /> Logout</button>
            <Button onClick={save} disabled={saving} className="btn-sheen rounded-full bg-helios-cyan hover:bg-helios-cyan/90 h-9 px-5" data-testid="admin-save">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={15} className="mr-1.5" /> Save</>}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="lg:sticky lg:top-[80px] h-max">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto rounded-2xl border border-helios-line bg-white p-2">
            {TABS.map(([id, label]) => (
              <button key={id} onClick={() => { setTab(id); if (id === "leads" && !leads) loadLeads(); }}
                className={`text-left whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${tab === id ? "bg-helios-slate text-white" : "text-slate-600 hover:bg-helios-mist"}`}
                data-testid={`admin-tab-${id}`}>{label}</button>
            ))}
          </nav>
        </aside>

        <main className="min-w-0" data-testid="admin-panel">
          {tab === "company" && (
            <Card>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Legal Name" value={g("companyInfo.legalName")} onChange={(v) => upd("companyInfo.legalName", v)} />
                <Field label="GST Number" value={g("companyInfo.gstNumber")} onChange={(v) => upd("companyInfo.gstNumber", v)} />
                <Field label="Email" value={g("companyInfo.email")} onChange={(v) => upd("companyInfo.email", v)} />
                <Field label="WhatsApp Number (digits only)" value={g("companyInfo.whatsappNumber")} onChange={(v) => upd("companyInfo.whatsappNumber", v)} />
                <Field label="Contact 1 Name" value={g("companyInfo.contact1Name")} onChange={(v) => upd("companyInfo.contact1Name", v)} />
                <Field label="Contact 1 Phone" value={g("companyInfo.contact1Phone")} onChange={(v) => upd("companyInfo.contact1Phone", v)} />
                <Field label="Contact 2 Name" value={g("companyInfo.contact2Name")} onChange={(v) => upd("companyInfo.contact2Name", v)} />
                <Field label="Contact 2 Phone" value={g("companyInfo.contact2Phone")} onChange={(v) => upd("companyInfo.contact2Phone", v)} />
                <Field label="IoT Dashboard URL" value={g("companyInfo.iotDashboardUrl")} onChange={(v) => upd("companyInfo.iotDashboardUrl", v)} />
                <Field label="Header Logo URL" value={g("companyInfo.logoUrl")} onChange={(v) => upd("companyInfo.logoUrl", v)} />
                <Field label="Footer Logo URL" value={g("companyInfo.footerLogoUrl")} onChange={(v) => upd("companyInfo.footerLogoUrl", v)} />
                <Field label="Favicon URL" value={g("companyInfo.faviconUrl")} onChange={(v) => upd("companyInfo.faviconUrl", v)} />
                <Field label="Logo Height (px)" type="number" value={g("companyInfo.logoHeightPx")} onChange={(v) => upd("companyInfo.logoHeightPx", v)} />
              </div>
              <Area label="Works Address" value={g("companyInfo.worksAddress")} onChange={(v) => upd("companyInfo.worksAddress", v)} />
            </Card>
          )}

          {tab === "banner" && (
            <Card>
              <Toggle label="Banner Enabled" value={g("announcementBanner.isEnabled")} onChange={(v) => upd("announcementBanner.isEnabled", v)} />
              <Field label="Message" value={g("announcementBanner.message")} onChange={(v) => upd("announcementBanner.message", v)} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Button Text" value={g("announcementBanner.buttonText")} onChange={(v) => upd("announcementBanner.buttonText", v)} />
                <Field label="Button Link (URL or #anchor)" value={g("announcementBanner.buttonLink")} onChange={(v) => upd("announcementBanner.buttonLink", v)} />
              </div>
            </Card>
          )}

          {tab === "analytics" && (
            <Card>
              <Field label="GA4 Measurement ID" value={g("analyticsAndTracking.ga4MeasurementId")} onChange={(v) => upd("analyticsAndTracking.ga4MeasurementId", v)} placeholder="G-XXXXXXXXXX" />
              <Field label="GTM Container ID" value={g("analyticsAndTracking.gtmContainerId")} onChange={(v) => upd("analyticsAndTracking.gtmContainerId", v)} placeholder="GTM-XXXXXXX" />
              <Field label="Google Search Console Verification" value={g("analyticsAndTracking.gscVerificationTag")} onChange={(v) => upd("analyticsAndTracking.gscVerificationTag", v)} />
              <p className="text-xs text-slate-400">JSON-LD Organization, Product & FAQ schema are auto-generated from your content.</p>
            </Card>
          )}

          {tab === "security" && (
            <Card>
              <Field label="Admin Passcode (leave blank to keep current)" type="password" value={g("adminSecurity.passcode")} onChange={(v) => upd("adminSecurity.passcode", v)} placeholder="••••••••" />
              <p className="text-xs text-slate-400">Changing this updates the passcode after you Save. You'll use the new one next login.</p>
            </Card>
          )}

          {tab === "social" && (
            <Card>
              <div className="grid sm:grid-cols-2 gap-4">
                {["instagram", "facebook", "linkedin", "twitter", "indiamart", "webCatalog"].map((k) => (
                  <Field key={k} label={k} value={g(`socialLinks.${k}`)} onChange={(v) => upd(`socialLinks.${k}`, v)} />
                ))}
              </div>
            </Card>
          )}

          {tab === "impact" && (
            <Card>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="MWp Cleaned" value={g("impactCounters.mwCleaned")} onChange={(v) => upd("impactCounters.mwCleaned", v)} />
                <Field label="Cycles Executed" value={g("impactCounters.cyclesExecuted")} onChange={(v) => upd("impactCounters.cyclesExecuted", v)} />
                <Field label="Water Saved" value={g("impactCounters.waterSaved")} onChange={(v) => upd("impactCounters.waterSaved", v)} />
                <Field label="Dust Removal %" value={g("impactCounters.dustRemovalPct")} onChange={(v) => upd("impactCounters.dustRemovalPct", v)} />
              </div>
            </Card>
          )}

          {tab === "products" && (
            <div className="space-y-5">
              {(g("productSeries", [])).map((s, si) => (
                <Card key={s.id || si}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-slate-400">SERIES {si + 1}</span>
                    <button onClick={() => upd("productSeries", g("productSeries").filter((_, i) => i !== si))} className="text-red-500 text-xs inline-flex items-center gap-1"><Trash2 size={13} /> Remove series</button>
                  </div>
                  <Field label="Series Name" value={s.seriesName} onChange={(v) => upd(`productSeries.${si}.seriesName`, v)} />
                  <Area label="Description" value={s.description} onChange={(v) => upd(`productSeries.${si}.description`, v)} />
                  <div className="space-y-4 pl-3 border-l-2 border-helios-line">
                    {(s.models || []).map((m, mi) => (
                      <div key={m.id || mi} className="rounded-xl bg-helios-mist p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[11px] text-slate-400">MODEL {mi + 1}</span>
                          <button onClick={() => upd(`productSeries.${si}.models`, s.models.filter((_, i) => i !== mi))} className="text-red-500 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <Field label="Model Name" value={m.name} onChange={(v) => upd(`productSeries.${si}.models.${mi}.name`, v)} />
                          <Field label="Starting Price (₹)" type="number" value={m.startingPrice} onChange={(v) => upd(`productSeries.${si}.models.${mi}.startingPrice`, v)} />
                        </div>
                        <Field label="Tagline" value={m.tagline} onChange={(v) => upd(`productSeries.${si}.models.${mi}.tagline`, v)} />
                        <Field label="Image URL" value={m.imageUrl} onChange={(v) => upd(`productSeries.${si}.models.${mi}.imageUrl`, v)} />
                        <Field label="Datasheet PDF URL" value={m.datasheetPdfUrl} onChange={(v) => upd(`productSeries.${si}.models.${mi}.datasheetPdfUrl`, v)} />
                        <Area label="Specs (one per line: Label | Value)" rows={5} value={specsToText(m.specs)} onChange={(v) => upd(`productSeries.${si}.models.${mi}.specs`, textToSpecs(v))} />
                        <Area label="Key Highlights (one per line)" value={listToText(m.keyHighlights)} onChange={(v) => upd(`productSeries.${si}.models.${mi}.keyHighlights`, textToList(v))} />
                        <div className="flex gap-2">
                          <Toggle label="Active" value={m.isActive} onChange={(v) => upd(`productSeries.${si}.models.${mi}.isActive`, v)} />
                          <Toggle label="Featured" value={m.isFeatured} onChange={(v) => upd(`productSeries.${si}.models.${mi}.isFeatured`, v)} />
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" className="rounded-lg" onClick={() => upd(`productSeries.${si}.models`, [...(s.models || []), { id: uid(), name: "New Model", tagline: "", startingPrice: 150000, imageUrl: "", datasheetPdfUrl: "", specs: [], keyHighlights: [], isActive: true, isFeatured: false }])}><Plus size={14} className="mr-1" /> Add model</Button>
                  </div>
                </Card>
              ))}
              <Button variant="outline" className="rounded-lg" onClick={() => upd("productSeries", [...g("productSeries", []), { id: uid(), seriesName: "New Series", description: "", models: [] }])}><Plus size={14} className="mr-1" /> Add series</Button>
            </div>
          )}

          {tab === "om" && (
            <div className="space-y-5">
              <Card>
                <p className="font-display font-bold text-helios-slate">Maintenance Pillars</p>
                {(g("omServices.pillars", [])).map((p, i) => (
                  <div key={p.id || i} className="rounded-xl bg-helios-mist p-4 space-y-3">
                    <div className="flex justify-between"><span className="font-mono text-[11px] text-slate-400">PILLAR {i + 1}</span>
                      <button onClick={() => upd("omServices.pillars", g("omServices.pillars").filter((_, x) => x !== i))} className="text-red-500 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button></div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Title" value={p.title} onChange={(v) => upd(`omServices.pillars.${i}.title`, v)} />
                      <Field label="Frequency" value={p.frequency} onChange={(v) => upd(`omServices.pillars.${i}.frequency`, v)} />
                    </div>
                    <Area label="Description" value={p.description} onChange={(v) => upd(`omServices.pillars.${i}.description`, v)} />
                    <div className="flex flex-wrap gap-3 items-center">
                      <label className="text-sm"><Label>Icon</Label>
                        <select value={p.iconName} onChange={(e) => upd(`omServices.pillars.${i}.iconName`, e.target.value)} className="rounded-lg border border-helios-line h-10 px-3 text-sm">
                          {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                        </select>
                      </label>
                      <Toggle label="Active" value={p.isActive} onChange={(v) => upd(`omServices.pillars.${i}.isActive`, v)} />
                    </div>
                  </div>
                ))}
                <Button variant="outline" className="rounded-lg" onClick={() => upd("omServices.pillars", [...g("omServices.pillars", []), { id: uid(), title: "New Pillar", frequency: "", description: "", iconName: "wrench", isActive: true }])}><Plus size={14} className="mr-1" /> Add pillar</Button>
              </Card>
              <Card>
                <p className="font-display font-bold text-helios-slate">Water Quality Standards</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {["conductivity", "tds", "ph", "hardness", "turbidity"].map((k) => (
                    <Field key={k} label={k} value={g(`omServices.waterStandards.${k}`)} onChange={(v) => upd(`omServices.waterStandards.${k}`, v)} />
                  ))}
                </div>
              </Card>
            </div>
          )}

          {tab === "gallery" && (
            <div className="space-y-5">
              <Card>
                <Field label="Hero Video URL (YouTube)" value={g("galleryMedia.heroVideoUrl")} onChange={(v) => upd("galleryMedia.heroVideoUrl", v)} />
                <Field label="Hero Background Image URL" value={g("galleryMedia.heroBgImageUrl")} onChange={(v) => upd("galleryMedia.heroBgImageUrl", v)} />
              </Card>
              <Card>
                <p className="font-display font-bold text-helios-slate">Project Films</p>
                {(g("galleryMedia.projects", [])).map((p, i) => (
                  <div key={p.id || i} className="rounded-xl bg-helios-mist p-4 space-y-3">
                    <div className="flex justify-between"><span className="font-mono text-[11px] text-slate-400">PROJECT {i + 1}</span>
                      <button onClick={() => upd("galleryMedia.projects", g("galleryMedia.projects").filter((_, x) => x !== i))} className="text-red-500 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button></div>
                    <Field label="Title" value={p.title} onChange={(v) => upd(`galleryMedia.projects.${i}.title`, v)} />
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Location" value={p.location} onChange={(v) => upd(`galleryMedia.projects.${i}.location`, v)} />
                      <Field label="Capacity" value={p.capacity} onChange={(v) => upd(`galleryMedia.projects.${i}.capacity`, v)} />
                      <Field label="YouTube ID or URL" value={p.mediaUrl} onChange={(v) => upd(`galleryMedia.projects.${i}.mediaUrl`, v)} />
                      <label><Label>Category</Label>
                        <select value={p.categoryTag} onChange={(e) => upd(`galleryMedia.projects.${i}.categoryTag`, e.target.value)} className="w-full rounded-lg border border-helios-line h-10 px-3 text-sm">
                          {["Drone Footage", "Rooftop Projects", "Ground Mount"].map((c) => <option key={c}>{c}</option>)}
                        </select>
                      </label>
                    </div>
                  </div>
                ))}
                <Button variant="outline" className="rounded-lg" onClick={() => upd("galleryMedia.projects", [...g("galleryMedia.projects", []), { id: uid(), title: "New Project", location: "", capacity: "", mediaType: "youtube", mediaUrl: "", categoryTag: "Ground Mount", isFeatured: true }])}><Plus size={14} className="mr-1" /> Add project</Button>
              </Card>
            </div>
          )}

          {tab === "clients" && (
            <Card>
              {(g("clientLogos", [])).map((c, i) => (
                <div key={c.id || i} className="grid sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end rounded-xl bg-helios-mist p-3">
                  <Field label="Client Name" value={c.clientName} onChange={(v) => upd(`clientLogos.${i}.clientName`, v)} />
                  <Field label="Logo URL (optional)" value={c.logoUrl} onChange={(v) => upd(`clientLogos.${i}.logoUrl`, v)} />
                  <Toggle label="Active" value={c.isActive} onChange={(v) => upd(`clientLogos.${i}.isActive`, v)} />
                  <button onClick={() => upd("clientLogos", g("clientLogos").filter((_, x) => x !== i))} className="h-10 w-10 flex items-center justify-center rounded-lg bg-red-50 text-red-500"><Trash2 size={16} /></button>
                </div>
              ))}
              <Button variant="outline" className="rounded-lg" onClick={() => upd("clientLogos", [...g("clientLogos", []), { id: uid(), clientName: "New Client", logoUrl: "", websiteUrl: "", isActive: true }])}><Plus size={14} className="mr-1" /> Add client</Button>
            </Card>
          )}

          {tab === "badges" && (
            <Card>
              {(g("certificationsAndBadges", [])).map((b, i) => (
                <div key={b.id || i} className="grid sm:grid-cols-[1fr_1fr_auto_auto_auto] gap-3 items-end rounded-xl bg-helios-mist p-3">
                  <Field label="Title" value={b.title} onChange={(v) => upd(`certificationsAndBadges.${i}.title`, v)} />
                  <Field label="Subtitle" value={b.subtitle} onChange={(v) => upd(`certificationsAndBadges.${i}.subtitle`, v)} />
                  <label><Label>Icon</Label>
                    <select value={b.iconName} onChange={(e) => upd(`certificationsAndBadges.${i}.iconName`, e.target.value)} className="rounded-lg border border-helios-line h-10 px-2 text-sm">
                      {ICON_OPTIONS.map((ic) => <option key={ic}>{ic}</option>)}
                    </select>
                  </label>
                  <Toggle label="Active" value={b.isActive} onChange={(v) => upd(`certificationsAndBadges.${i}.isActive`, v)} />
                  <button onClick={() => upd("certificationsAndBadges", g("certificationsAndBadges").filter((_, x) => x !== i))} className="h-10 w-10 flex items-center justify-center rounded-lg bg-red-50 text-red-500"><Trash2 size={16} /></button>
                </div>
              ))}
              <Button variant="outline" className="rounded-lg" onClick={() => upd("certificationsAndBadges", [...g("certificationsAndBadges", []), { id: uid(), title: "New Badge", subtitle: "", iconName: "badge", isActive: true }])}><Plus size={14} className="mr-1" /> Add badge</Button>
            </Card>
          )}

          {tab === "testimonials" && (
            <Card>
              {(g("testimonials", [])).map((t, i) => (
                <div key={t.id || i} className="rounded-xl bg-helios-mist p-4 space-y-3">
                  <div className="flex justify-between"><span className="font-mono text-[11px] text-slate-400">REVIEW {i + 1}</span>
                    <button onClick={() => upd("testimonials", g("testimonials").filter((_, x) => x !== i))} className="text-red-500 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button></div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Field label="Client Name" value={t.clientName} onChange={(v) => upd(`testimonials.${i}.clientName`, v)} />
                    <Field label="Designation" value={t.designation} onChange={(v) => upd(`testimonials.${i}.designation`, v)} />
                    <Field label="Company" value={t.companyName} onChange={(v) => upd(`testimonials.${i}.companyName`, v)} />
                  </div>
                  <Area label="Quote" value={t.quote} onChange={(v) => upd(`testimonials.${i}.quote`, v)} />
                  <div className="grid sm:grid-cols-3 gap-3 items-end">
                    <Field label="Rating (1-5)" type="number" value={t.rating} onChange={(v) => upd(`testimonials.${i}.rating`, v)} />
                    <Field label="Photo URL" value={t.img} onChange={(v) => upd(`testimonials.${i}.img`, v)} />
                    <Toggle label="Published" value={t.isPublished} onChange={(v) => upd(`testimonials.${i}.isPublished`, v)} />
                  </div>
                </div>
              ))}
              <Button variant="outline" className="rounded-lg" onClick={() => upd("testimonials", [...g("testimonials", []), { id: uid(), clientName: "New Client", designation: "", companyName: "", rating: 5, quote: "", img: "", isPublished: true }])}><Plus size={14} className="mr-1" /> Add testimonial</Button>
            </Card>
          )}

          {tab === "roi" && (
            <Card>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Default Capacity (kWp)" type="number" value={g("roiDefaults.defaultCapacityKwp")} onChange={(v) => upd("roiDefaults.defaultCapacityKwp", v)} />
                <Field label="Default Daily Yield" type="number" value={g("roiDefaults.defaultYieldKwh")} onChange={(v) => upd("roiDefaults.defaultYieldKwh", v)} />
                <Field label="Default Tariff (₹)" type="number" value={g("roiDefaults.defaultTariffRs")} onChange={(v) => upd("roiDefaults.defaultTariffRs", v)} />
                <Field label="Default Soiling %" type="number" value={g("roiDefaults.defaultSoilingPct")} onChange={(v) => upd("roiDefaults.defaultSoilingPct", v)} />
                <Field label="Default Manual Cost (₹/mo)" type="number" value={g("roiDefaults.defaultManualCostMonthly")} onChange={(v) => upd("roiDefaults.defaultManualCostMonthly", v)} />
                <Field label="Robot Maintenance ₹/yr" type="number" value={g("roiDefaults.robotMaintenanceAnnualPerUnit")} onChange={(v) => upd("roiDefaults.robotMaintenanceAnnualPerUnit", v)} />
                <Field label="Price per Robot (₹)" type="number" value={g("roiDefaults.pricePerRobot")} onChange={(v) => upd("roiDefaults.pricePerRobot", v)} />
              </div>
            </Card>
          )}

          {tab === "faqs" && (
            <Card>
              {(g("faqs", [])).map((f, i) => (
                <div key={f.id || i} className="rounded-xl bg-helios-mist p-4 space-y-3">
                  <div className="flex justify-between"><span className="font-mono text-[11px] text-slate-400">FAQ {i + 1}</span>
                    <button onClick={() => upd("faqs", g("faqs").filter((_, x) => x !== i))} className="text-red-500 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button></div>
                  <Field label="Question" value={f.question} onChange={(v) => upd(`faqs.${i}.question`, v)} />
                  <Area label="Answer" value={f.answer} onChange={(v) => upd(`faqs.${i}.answer`, v)} />
                </div>
              ))}
              <Button variant="outline" className="rounded-lg" onClick={() => upd("faqs", [...g("faqs", []), { id: uid(), question: "New question?", answer: "" }])}><Plus size={14} className="mr-1" /> Add FAQ</Button>
            </Card>
          )}

          {tab === "leads" && (
            <Card>
              <div className="flex items-center justify-between">
                <p className="font-display font-bold text-helios-slate flex items-center gap-2"><Inbox size={18} /> Captured Leads</p>
                <Button variant="outline" className="rounded-lg" onClick={loadLeads}>Refresh</Button>
              </div>
              {leads === null ? <p className="text-slate-500 text-sm">Loading…</p> : leads.length === 0 ? <p className="text-slate-500 text-sm">No leads yet.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left" data-testid="admin-leads-table">
                    <thead><tr className="text-slate-400 font-mono text-[11px] uppercase"><th className="py-2 pr-4">Type</th><th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Email</th><th className="py-2 pr-4">Phone</th><th className="py-2 pr-4">Company</th></tr></thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l.id} className="border-t border-helios-line">
                          <td className="py-2 pr-4"><span className="rounded-full bg-helios-mist px-2 py-0.5 text-[11px]">{l.lead_type}</span></td>
                          <td className="py-2 pr-4 font-medium text-helios-slate">{l.name}</td>
                          <td className="py-2 pr-4 text-slate-600">{l.email}</td>
                          <td className="py-2 pr-4 font-mono text-xs">{l.phone}</td>
                          <td className="py-2 pr-4 text-slate-600">{l.company || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </main>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}

// PUT save using stored passcode header
async function axiosSave(draft, pass) {
  const axios = (await import("axios")).default;
  return axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/content`, draft, { headers: { "X-Admin-Passcode": pass } });
}
